import { useMemo, useState } from 'react'
import { Check, ChevronsUpDown, Landmark, Plus, Search } from 'lucide-react'
import { Combobox } from '@/components/Combobox'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/Modal'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { BankAccountInlineForm } from '@/components/BankAccountInlineForm'
import { cn } from '@/lib/cn'
import { useBankAccounts } from '@/features/bankAccounts/queries'
import type { BankAccount } from '@/features/bankAccounts/types'

const BANK_ICON = <Landmark className="size-4 text-primary" />
const ADD_ICON = <Plus className="size-4 text-muted-foreground" />

interface SingleProps {
  mode?: 'single'
  value: number | null
  onChange: (id: number | null) => void
  includeAddNew?: boolean
  placeholder?: string
  className?: string
}

interface MultiProps {
  mode: 'multi'
  value: number[]
  onChange: (ids: number[]) => void
  placeholder?: string
  className?: string
}

type Props = SingleProps | MultiProps

function formatAccount(a: BankAccount): string {
  const mask = a.mask ? `····${a.mask}` : ''
  if (a.bank_name) return `${a.bank_name} ${mask}`.trim()
  if (a.label) return `${a.label}${mask ? ' ' + mask : ''}`
  return mask || `Account ${a.id}`
}

export function BankAccountPicker(props: Props) {
  const listQ = useBankAccounts()
  const accounts = listQ.data?.bank_accounts ?? []
  const [adding, setAdding] = useState(false)

  if (props.mode === 'multi') {
    return <MultiPicker {...props} accounts={accounts} />
  }

  const options: Array<{ value: number; label: string; icon?: React.ReactNode }> =
    accounts.map((a) => ({ value: a.id, label: formatAccount(a), icon: BANK_ICON }))
  if (props.includeAddNew) {
    options.push({ value: -1, label: 'Add new account', icon: ADD_ICON })
  }

  const handleChange = (v: number | null) => {
    if (v === -1) {
      setAdding(true)
      return
    }
    props.onChange(v ?? null)
  }

  return (
    <>
      <Combobox
        value={props.value}
        onChange={handleChange}
        options={options}
        placeholder={props.placeholder ?? 'Select a bank account'}
        className={props.className}
      />
      {adding ? (
        <Modal
          open={true}
          onOpenChange={(v) => !v && setAdding(false)}
          title="Add bank account"
          className="max-w-md"
        >
          <BankAccountInlineForm
            onCreated={(a) => {
              setAdding(false)
              props.onChange(a.id)
            }}
            onCancel={() => setAdding(false)}
          />
        </Modal>
      ) : null}
    </>
  )
}

function MultiPicker({
  value, onChange, placeholder, className, accounts,
}: MultiProps & { accounts: BankAccount[] }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return accounts
    return accounts.filter((a) => {
      const hay = `${a.bank_name ?? ''} ${a.label ?? ''} ${a.mask ?? ''}`.toLowerCase()
      return hay.includes(q)
    })
  }, [accounts, query])

  const selected = useMemo(() => new Set(value), [value])
  const visibleIds = useMemo(() => filtered.map((a) => a.id), [filtered])
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id))
  const someVisibleSelected = visibleIds.some((id) => selected.has(id))

  const toggle = (id: number) => {
    if (selected.has(id)) {
      onChange(value.filter((v) => v !== id))
    } else {
      onChange([...value, id])
    }
  }

  const selectAllVisible = () => {
    const next = new Set(value)
    for (const id of visibleIds) next.add(id)
    onChange(Array.from(next))
  }

  const clearAll = () => onChange([])

  const summary =
    value.length === 0
      ? (placeholder ?? 'All accounts')
      : value.length === 1
        ? (() => {
            const a = accounts.find((x) => x.id === value[0])
            return a ? formatAccount(a) : '1 account'
          })()
        : `${value.length} accounts`

  return (
    <div className={cn('w-full', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal"
          >
            <span className="flex min-w-0 items-center gap-2">
              <Landmark className="size-4 shrink-0 text-primary" />
              <span className={cn('truncate', value.length === 0 && 'text-muted-foreground')}>
                {summary}
              </span>
            </span>
            <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-[--radix-popover-trigger-width] min-w-[min(280px,90vw)] max-w-[90vw] p-0"
        >
          <div className="flex items-center gap-2 border-b p-2">
            <Search className="size-4 opacity-60" />
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.currentTarget.value)}
              placeholder="Search bank or last-4…"
              className="h-8 border-0 bg-transparent p-0 focus-visible:ring-0"
            />
          </div>
          <div className="flex items-center justify-between border-b px-3 py-2 text-xs">
            <label className="flex cursor-pointer items-center gap-1.5 font-medium">
              <Checkbox
                checked={
                  allVisibleSelected
                    ? true
                    : someVisibleSelected
                      ? 'indeterminate'
                      : false
                }
                onCheckedChange={() =>
                  allVisibleSelected ? clearAll() : selectAllVisible()
                }
                disabled={visibleIds.length === 0}
                aria-label="Select all visible accounts"
              />
              {allVisibleSelected ? 'Unselect all' : 'Select all'}
              {query && filtered.length !== accounts.length ? (
                <span className="text-muted-foreground"> ({filtered.length})</span>
              ) : null}
            </label>
            <button
              type="button"
              onClick={clearAll}
              disabled={value.length === 0}
              className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-40"
            >
              Clear ({value.length})
            </button>
          </div>
          <div
            className="max-h-[min(60vh,420px)] overflow-y-auto overscroll-contain"
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            {accounts.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                No bank accounts yet. Upload a statement or connect Plaid to add one.
              </div>
            ) : filtered.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                No matches for &ldquo;{query}&rdquo;.
              </div>
            ) : (
              <ul role="listbox" aria-multiselectable="true" className="py-1">
                {filtered.map((a) => {
                  const isSelected = selected.has(a.id)
                  return (
                    <li key={a.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        onClick={() => toggle(a.id)}
                        className={cn(
                          'flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground',
                          isSelected && 'bg-accent/60',
                        )}
                      >
                        <Checkbox checked={isSelected} aria-hidden="true" tabIndex={-1} />
                        <Landmark className="size-4 shrink-0 text-primary" />
                        <span className="min-w-0 flex-1 truncate">{formatAccount(a)}</span>
                        {isSelected ? (
                          <Check className="size-4 shrink-0 opacity-70" />
                        ) : null}
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
          <div className="flex items-center justify-between border-t px-3 py-2">
            <span className="text-xs text-muted-foreground">
              {value.length === 0 ? 'All accounts (default)' : `${value.length} selected`}
            </span>
            <Button size="sm" onClick={() => setOpen(false)}>
              Done
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
